import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { Toaster } from 'react-hot-toast';

import { usePermissions } from './hooks/usePermissions';
import { LeadProvider } from './context/LeadContext';
import { NotificationProvider } from './components/common/NotificationProvider';
import WithCustomAlerts from './components/common/WithCustomAlerts';
import Sidebar from './components/common/Sidebar/Sidebar';
import Header from './components/common/Header/Header';
import ScrollToTop from './components/common/ScrollToTop';
import Login from './pages/auth/Login';
import AdminDashboard from './pages/dashboard/AdminDashboard';
import HRDashboard from './pages/dashboard/HRDashboard';
import HRMSContainer from './pages/hrms/HRMSContainer';
import RecruitmentModule from './pages/hrms/RecruitmentModule';
import EmployeeDashboard from './pages/dashboard/EmployeeDashboard';
import JobPosts from './pages/hrms/recruitment/JobPosts';
import CreateJob from './pages/hrms/recruitment/CreateJob';
import ViewJob from './pages/hrms/recruitment/ViewJob';

import JobApplication from './pages/career/JobApplication';
import Career from './pages/career/Career';
import OfferAcceptance from './pages/OfferAcceptance';
import OfferResponse from './pages/OfferResponse';
import CandidateDocumentUploadPortal from './pages/CandidateDocumentUploadPortal';
import Settings from './pages/profile/Settings';
import DocumentVerification from './pages/hrms/onboarding/DocumentVerification';


import BackgroundVerification from './pages/hrms/onboarding/BackgroundVerification';
import InitiateBackgroundVerification from './pages/hrms/onboarding/InitiateBackgroundVerification';
import ViewBackgroundVerification from './pages/hrms/onboarding/ViewBackgroundVerification';
import Orientation from './pages/hrms/onboarding/Orientation';
import CreateOrientation from './pages/hrms/onboarding/CreateOrientation';
import ViewOrientation from './pages/hrms/onboarding/ViewOrientation';
import SendOrientationInvites from './pages/hrms/onboarding/SendOrientationInvites';
import CloneOrientation from './pages/hrms/onboarding/CloneOrientation';

import OrientationInviteResponse from './pages/hrms/onboarding/OrientationInviteResponse';
import PayrollDashboard from './pages/hrms/payroll/PayrollDashboard';
import SalaryStructures from './pages/hrms/payroll/SalaryStructures';
import CreateSalaryStructure from './pages/hrms/payroll/CreateSalaryStructure';
import PayrollRun from './pages/hrms/payroll/PayrollRun';
import PayslipManagement from './pages/hrms/payroll/PayslipManagement';
import PayslipBatchDetails from './pages/hrms/payroll/PayslipBatchDetails';
import PayrollModule from './pages/hrms/payroll/PayrollModule';
import PerformanceModule from './pages/hrms/PerformanceModule';
import TrainingModule from './pages/hrms/TrainingModule';
import CourseDetailPage from './pages/hrms/training-development/CourseDetailPage';
import LearningPathDetailPage from './pages/hrms/training-development/LearningPathDetailPage';
import ExitModule from './pages/hrms/ExitModule';
import CompliancePolicyModule from './pages/hrms/CompliancePolicyModule';
import HRAnalyticsModule from './pages/hrms/HRAnalyticsModule';
import OnboardingModule from './pages/hrms/OnboardingModule';
import AssignmentDetailsPage from './pages/hrms/training-development/AssignmentDetailsPage';
import { EmployeeModule, EmployeeList, EditEmployee, DocumentManager, TransferRoleChange } from './pages/hrms/employee-database';
import DepartmentRoleMapping from './pages/hrms/system/DepartmentRoleMapping';
import GlobalLeaveConfiguration from './pages/hrms/system/GlobalLeaveConfiguration';
import FieldSalesDashboard from './pages/crm/field-sales/FieldSalesDashboard';
import RoutePlans from './pages/crm/field-sales/RoutePlans';
import RoutePlanDetails from './pages/crm/field-sales/RoutePlanDetails';


import BackOfficeModule from './pages/hrms/BackOfficeModule';
// LeaveManagement import removed - COMMENTED OUT
import AttendanceLeaveModule from './pages/hrms/AttendanceLeaveModule';
import { AttendanceDashboard, AttendanceKpiDashboard, AttendanceLogs, Timesheet, AttendanceRegularization, LeaveRequests, LeavePolicies, HolidayCalendar, ShiftRoster } from './pages/hrms/attendance-leaves';
import { ExitRequests, NoDuesClearance, RelievingLetter, RelievingLetterDetail, ExperienceCertificate } from './pages/hrms/exit-management';
import { PolicyLibrary, Acknowledgments, StatutoryCompliance, AuditLogDetail } from './pages/hrms/compliance-policy';
import PublicPolicyAcknowledgeRedirect from './pages/hrms/compliance-policy/PublicPolicyAcknowledgeRedirect';
import { AddEmployee, EmployeeProfile, EmployeeTransfer, BulkUpload } from './pages/hrms/employee-database';
import LocationManagement from './pages/hrms/system/LocationManagement';
import ConfigureManagement from './pages/crm/system-management/ConfigureManagement';
import WebsiteMaster from './pages/crm/system-management/WebsiteMaster';
import { RecruitmentJobs, CandidatePipeline, InterviewScheduling, ViewInterview, OfferManagement, ViewOffer, EditOffer, RecruitmentAnalytics, CandidateProfileManagement, AddCandidate, ViewCandidate, OfferCommunicationLogs, NewCommunication, EditCommunication, ReplyCommunication, ForwardCommunication, ViewCommunication } from './pages/hrms/recruitment';
import {
  HRAnalyticsDashboard, AttritionAnalysis, RecruitmentFunnel, SalaryTrends, ComplianceReminders,
  AddComplianceReminder, ComplianceReminderDetail, EditComplianceReminder, ReminderNotificationSettings,
  PolicyManagement, NewPolicy, ViewPolicy, EditPolicy, DownloadPolicy, SendPolicyReminder, PolicySettings,
  AuditLogs as AnalyticsAuditLogs
} from './pages/hrms/analytics-reporting';
import {
  LeadDashboard, LeadSources,
  QuoteBuilder,
  // OrderManagement, OrderDashboard, InventorySync, DispatchTracking, BatchExpiryManagement,
  CustomerProfiles, CRMAnalytics, SalesDashboard,
  PartyFinancials, /* QuoteOrderDashboard, */ CRMSalesDashboard, Customer360, Customer360List, CustomerForm, // CommsInbox,
  TargetsDashboard, TargetBuilder, /* TeamView, */ Schedules, TargetAchievement, CreatedTargets, TargetDetails,
  TicketsFeedback, /* Campaigns, */ /* CallerPopups, */ /* ChatbotSelfService, */ ConvertOpportunity,
  LeadActivities, LeadAssignment, AssignmentRuleDetail, LeadDetail360, LeadListFilters, LeadScoringQualification, LogActivity, ActivityDetail,
  LeadSourcesCapture, ManualLeadEntry, /* TenderLead, */ /* CloseWonLost, ForecastsTargets, */
  // OpportunityKanban, 
  /* PipelineDashboard, PipelineSettings, TasksFollowups, */
  QuotationDashboard, SendTrack, VersionHistory,
  // NEW – Shipway shipment pages
  // ShipmentAnalytics
} from './pages/crm';
// import TenderForm from './pages/crm/lead-management/TenderForm';
// import TenderDetail from './pages/crm/lead-management/TenderDetail';
import ScoringRuleDetail from './pages/crm/lead-management/ScoringRuleDetail';
import QualificationCriteriaDetail from './pages/crm/lead-management/QualificationCriteriaDetail';
import LinkedInCallback from './pages/crm/lead-management/LinkedInCallback';
import LeadManagementModule from './pages/crm/lead-management/LeadManagementModule';
import CreateQuotesList from './pages/crm/quotation-proposal/CreateQuotesList';
import DoiDashboard from './pages/crm/quotation-proposal/DoiDashboard';
import DoiView from './pages/crm/quotation-proposal/DoiView';
import PublicQuotationView from './pages/crm/quotation-proposal/PublicQuotationView';
import PublicTicketView from './pages/public/PublicTicketView';
import TicketDetails from './pages/crm/customer-relationship/TicketDetails';
import MyAttendance from './pages/profile/MyAttendance';
import MyProfile from './pages/profile/MyProfile';
import MyWFHRequests from './pages/profile/MyWFHRequests';
const ApplyLeave = React.lazy(() => import('./pages/profile/ApplyLeave'));
const MyPayslips = React.lazy(() => import('./pages/profile/MyPayslips'));
const PayslipDetailsPage = React.lazy(() => import('./pages/hrms/payroll/PayslipDetailsPage'));
const MyTraining = React.lazy(() => import('./pages/profile/MyTraining'));
const MyPerformance = React.lazy(() => import('./pages/profile/MyPerformance'));
const SubmitResignation = React.lazy(() => import('./pages/profile/SubmitResignation'));
const MyDashboard = React.lazy(() => import('./pages/profile/MyDashboard'));
const SaraChat = React.lazy(() => import('./components/common/SaraAI/SaraChat'));
const AICopilotPage = React.lazy(() => import('./pages/aiCopilot/AICopilotPage'));

import PoliciesCompliance from './pages/profile/PoliciesCompliance';
import Notifications from './pages/profile/Notifications';
import WorkFromHomeApprovals from './pages/hrms/attendance-leaves/WorkFromHomeApprovals';

// Helper for Lead Detail Redirect
const LeadDetailRedirect = () => {
  const { leadId } = useParams();
  return <Navigate to={`/crm/leads?tab=list&action=view_lead&id=${leadId}`} replace />;
};

// Helper for Log Activity Redirect
const LogActivityRedirect = () => {
  const { id } = useParams(); // leadId
  const [searchParams] = useSearchParams();
  const activityId = searchParams.get('activityId');
  const isEdit = searchParams.get('edit') === 'true';

  if (isEdit && activityId) {
    return <Navigate to={`/crm/leads?tab=activities&action=edit_activity&id=${activityId}`} replace />;
  }
  return <Navigate to={`/crm/leads?tab=activities&action=log_activity&id=${id}`} replace />;
}

// Component to handle HRMS route with optional view query param
const HRMSRouteHandler = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const viewParam = searchParams.get('view');
  // Use HRMSContainer if view param is present (e.g., for audit_logs from compliance_policy)
  // Otherwise use HRDashboard
  return viewParam ? <HRMSContainer /> : <HRDashboard />;
};

// Wrapper for Employee Module to handle legacy routes with params
const EmployeeModuleRouteHandler = ({ action, tab }) => {
  const { employeeId } = useParams(); // For /:employeeId routes
  return (
    <EmployeeModule
      initialTab={tab || 'employeeList'}
      initialAction={action}
      initialActionId={employeeId}
    />
  );
};

// Dashboard Router Component - Shows appropriate dashboard based on user role/permissions
const DashboardRouter = () => {
  const { user } = useAuth();
  const role = (user?.role || 'employee').toLowerCase();

  // 1. Employee Self-Service roles always see MyDashboard
  if (['employee', 'trainee', 'intern', 'user'].includes(role)) {
    return <MyDashboard />;
  }

  // 2. Super Admin always sees AdminDashboard
  if (role === 'super_admin') {
    return <AdminDashboard />;
  }

  // Check if user has HRMS permissions
  const hasHRMSPermission = () => {
    if (['super_admin', 'hr_manager', 'admin'].includes(role)) return true;
    if (!user?.granularPermissions) return false;

    const hrmsPermissions = [
      'employee_management',
      'recruitment',
      'onboarding',
      'hr_backoffice',
      'attendance',
      'payroll',
      'performance',
      'training',
      'exit_management',
      'compliance_policy',
      'reports'
    ];

    return hrmsPermissions.some(permKey => {
      const permission = user.granularPermissions[permKey];
      return permission && permission.read === true;
    });
  };

  // Check if user has self_service permission
  const hasSelfServicePermission = () => {
    if (user?.role === 'super_admin') return true;
    if (!user?.granularPermissions) return false;
    const permission = user.granularPermissions['self_service'];
    return permission && permission.read === true;
  };

  // If user has self_service permission but no HRMS permissions, show employee dashboard
  if (hasSelfServicePermission() && !hasHRMSPermission()) {
    return <MyDashboard />;
  }

  // If user has HRMS administrative permissions, show admin/HR dashboard
  if (hasHRMSPermission()) {
    return <AdminDashboard />;
  }

  // Safe fallback for standard users is MyDashboard
  return <MyDashboard />;
};

// Login Redirect Router Component - Redirects to appropriate page based on user permissions after login
const LoginRedirectRouter = () => {
  const { user } = useAuth();
  const role = (user?.role || 'employee').toLowerCase();

  // Super admin always goes to main dashboard
  if (role === 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Standard employee self-service roles always redirect to /mydashboard
  if (['employee', 'trainee', 'intern', 'user'].includes(role)) {
    return <Navigate to="/mydashboard" replace />;
  }

  // Define module to first screen mapping in priority order
  // The first module with read permission will be the redirect target
  const moduleScreenMapping = [
    // CRM/Sales modules (highest priority for sales users)
    { permission: 'lead_management', path: '/lead_dashboard' },
    { permission: 'field_sales', path: '/field_sales_dashboard' },
    { permission: 'opportunity_management', path: '/Opportunity-dashboard' },
    { permission: 'quotation_management', path: '/crm/quotation-proposal/dashboard' },
    // { permission: 'order_management', path: '/order_management' },
    { permission: 'customer_management', path: '/customer_360' },

    // Logistics/Dispatch modules
    // { permission: 'dispatch_management', path: '/dispatch_tracking' },
    // { permission: 'logistics', path: '/crm/logistics/shipments' },

    // Support modules
    { permission: 'ticket_management', path: '/tickets_feedback' },
    { permission: 'customer_support', path: '/tickets_feedback' },

    // HRMS modules
    { permission: 'employee_management', path: '/employee_list' },
    { permission: 'recruitment', path: '/job_posts' },
    { permission: 'onboarding', path: '/document_verification' },
    { permission: 'hr_backoffice', path: '/hr_backoffice' },
    { permission: 'attendance', path: '/attendance_dashboard' },
    { permission: 'payroll', path: '/payroll_dashboard' },
    { permission: 'performance', path: '/performance_dashboard' },
    { permission: 'training', path: '/hrms/training' },
    { permission: 'exit_management', path: '/hrms/exit' },
    { permission: 'compliance_policy', path: '/hrms/compliance-policy' },
    { permission: 'reports', path: '/analytics_audit_logs' },

    // Employee self-service (lowest priority - for regular employees)
    { permission: 'self_service', path: '/mydashboard' }
  ];

  // Check user's granular permissions and find the first accessible module
  if (user?.granularPermissions) {
    for (const mapping of moduleScreenMapping) {
      const permission = user.granularPermissions[mapping.permission];
      if (permission && permission.read === true) {
        return <Navigate to={mapping.path} replace />;
      }
    }
  }

  // If HR/Admin role, default to admin dashboard; otherwise default to mydashboard
  if (['hr_manager', 'admin', 'hr_executive', 'sales_manager'].includes(role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/mydashboard" replace />;
};

// Placeholder Components for Future Development
const PlaceholderPage = ({ title, description }) => (
  <div className="bg-white rounded-lg shadow-sm p-8">
    <div className="text-center">
      <h1 className="text-3xl font-bold text-gray-900 mb-4">{title}</h1>
      <p className="text-gray-600 mb-8">{description}</p>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <p className="text-blue-800">
          🚧 This page is under development. The component will be created soon.
        </p>
      </div>
    </div>
  </div>
);

// Legacy Redirect Helper for Onboarding
const LegacyOnboardingRedirect = ({ tab, action }) => {
  const { id } = useParams();
  const searchParams = new URLSearchParams();
  if (tab) searchParams.set('tab', tab);
  if (action) searchParams.set('action', action);
  if (id) searchParams.set('id', id);
  return <Navigate to={`/hrms/onboarding?${searchParams.toString()}`} replace />;
};

const LegacyBackOfficeRedirect = ({ tab, action }) => {
  const { id } = useParams();
  const searchParams = new URLSearchParams();
  if (tab) searchParams.set('tab', tab);
  if (action) searchParams.set('action', action);
  if (id) searchParams.set('id', id);
  return <Navigate to={`/hrms/back-office?${searchParams.toString()}`} replace />;
};

// Protected Route Component - Permission-based only
const ProtectedRoute = ({ children, requiredPermission = null, requiredAction = 'read', requiredRole = null }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const { hasPermission } = usePermissions();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  // Check if user is authenticated by checking both context and localStorage
  const token = localStorage.getItem("auth_token");
  const userData = localStorage.getItem("user_data");

  // Basic token validation - check if token exists and has proper structure
  const isValidToken = token && token.length > 20; // Basic check - JWT tokens are longer

  // Check if user data exists and can be parsed
  let parsedUserData = null;
  if (userData) {
    try {
      parsedUserData = JSON.parse(userData);
    } catch (e) {
      // Invalid user data - log but don't clear immediately
      // Let AuthContext handle the cleanup to avoid race conditions

      // Don't clear here - let AuthContext handle it
      parsedUserData = null;
    }
  }

  // Check authentication - prioritize AuthContext state over localStorage
  // AuthContext is the source of truth after initial load
  const isActuallyAuthenticated = isAuthenticated || (isValidToken && parsedUserData);

  // Only redirect to login if we're absolutely sure user is not authenticated
  // Don't clear localStorage here - let AuthContext handle it
  if (!isActuallyAuthenticated) {
    // If AuthContext says not authenticated AND we have no valid token/data, redirect
    if (!isAuthenticated && !isValidToken) {

      return <Navigate to="/login" replace />;
    }
    // If we have valid token/data but AuthContext says not authenticated, 
    // might be a race condition - wait for AuthContext to catch up
    if (!isAuthenticated && isValidToken && parsedUserData) {

      // Show loading state briefly to let AuthContext catch up
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-agrosaf-green"></div>
        </div>
      );
    }
  }

  // Use user from context or parsed user data
  const currentUser = user || parsedUserData;

  // Role check
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (currentUser && !roles.includes(currentUser.role)) {

      return <Navigate to="/dashboard" replace />;
    }
  }

  // Permission check - REQUIRED for all protected routes
  if (requiredPermission) {
    if (!currentUser) {
      // No user data but permission required - might be loading, show loading

      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-agrosaf-green"></div>
        </div>
      );
    }

    // Check if user has the required permission
    const checkPermissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    const hasAccess = checkPermissions.some(perm => hasPermission(perm, requiredAction));

    if (!hasAccess) {
      // User doesn't have required permission - redirect to dashboard (not logout)

      return <Navigate to="/dashboard" replace />;
    }
  } else {
    // If no permission specified, allow access (for public authenticated routes like dashboard)
    // But log a warning for routes that should have permissions
    if (currentUser && currentUser.role !== 'super_admin' && !requiredRole) {

    }
  }

  return children;
};

// Main Layout Component
const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const mainContentRef = React.useRef(null);

  React.useEffect(() => {
    document.documentElement.classList.remove('dark');
    localStorage.setItem('app_theme', 'light');
  }, []);

  React.useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
    };
  }, []);
  // Load expanded items from localStorage on mount, or initialize based on current path
  const [expandedItems, setExpandedItems] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarExpandedItems');
      const savedState = saved ? JSON.parse(saved) : {};

      // Check current pathname and ensure orders_logistics is expanded if on orders/logistics pages
      const path = window.location.pathname;
      if (
        path === "/orders_logistics" ||
        path === "/order_management" ||
        path === "/inventory_sync" ||
        path === "/dispatch_tracking" ||
        path === "/batch_expiry"
      ) {
        return savedState;
      }

      return savedState;
    } catch {
      // If localStorage fails, check pathname
      const path = window.location.pathname;
      if (
        path === "/orders_logistics" ||
        path === "/order_management" ||
        path === "/inventory_sync" ||
        path === "/dispatch_tracking" ||
        path === "/batch_expiry"
      ) {
        return {};
      }
      return {};
    }
  });
  const [activeItem, setActiveItem] = useState("");
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");

  // Get active item from current path
  const getActiveItem = React.useCallback(() => {
    const path = location.pathname;
    const params = new URLSearchParams(location.search);

    if (path === "/ai-copilot") return "ai_copilot";
    if (path === "/dashboard") return "dashboard";
    if (path === "/mydashboard") return "my_dashboard";

    // Check for background verification tab in HRMS onboarding
    if (path === '/hrms/onboarding' && params.get('tab') === 'backgroundVerification') {
      return 'background_verification';
    }

    // --- HRMS MODULES ---

    // 1. Candidate Pipeline / Interview Pipeline
    if (
      path === '/candidate_pipeline' ||
      path === '/interview_scheduling' ||
      path.startsWith('/edit_interview/') ||
      path.startsWith('/view_interview/') ||
      (path === '/hrms/recruitment' && (params.get('tab') === 'pipeline' || params.get('tab') === 'hiringPipeline' || params.get('tab') === 'interviews'))
    ) return 'candidate_pipeline';

    // 2. Job Openings / Posts
    if (
      path === '/job_posts' ||
      path === '/create-job' ||
      path.startsWith('/edit-job/')
    ) return 'job_posts';

    // 3. Candidates / Candidate Profiles
    if (
      path === '/candidate_profile_management' ||
      path === '/add_candidate' ||
      path.startsWith('/edit_candidate/') ||
      path.startsWith('/view_candidate/') ||
      (path === '/hrms/recruitment' && (params.get('tab') === 'candidates' || params.get('tab') === 'candidateProfiles')) ||
      (path === '/hrms/back-office' && params.get('tab') === 'candidateProfiles')
    ) return 'candidate_profile_management';

    // 4. Recruitment Overview (Default for /recruitment, /hrms/recruitment, offers, analytics)
    if (
      path === '/recruitment' ||
      path === '/hrms/recruitment' ||
      path === '/offer_management' ||
      path.startsWith('/view_offer/') ||
      path.startsWith('/edit_offer/') ||
      path === '/recruitment_analytics'
    ) return 'recruitment';

    // Communication pages – context-sensitive:
    // Set communicationNavContext in sessionStorage before navigating:
    //   'hr_backoffice' → highlights HR Back-Office
    //   'recruitment' or no context → highlights Recruitment
    if (
      path === '/new_communication' ||
      path.startsWith('/edit_communication/') ||
      path.startsWith('/reply_communication/') ||
      path.startsWith('/forward_communication/') ||
      path.startsWith('/view_communication/')
    ) {
      const ctx = sessionStorage.getItem('communicationNavContext');
      sessionStorage.removeItem('communicationNavContext'); // consume once
      return ctx === 'hr_backoffice' ? 'hr_backoffice' : 'recruitment';
    }

    // Employee Database
    if (
      path === '/employee_database' ||
      path === '/employee_list' ||
      path === '/add_employee' ||
      path === '/bulk_upload' ||
      path.startsWith('/employee_profile/') ||
      path.startsWith('/edit_employee/')
    ) return 'employee_database';

    // Attendance & Leave
    if (
      path === '/attendance_dashboard' ||
      path === '/attendance_logs' ||
      path === '/attendance_regularization' ||
      path === '/leave_requests' ||
      path === '/leave_policies' ||
      path === '/holiday_calendar' ||
      path === '/shift_roster' ||
      path === '/timesheet'
    ) return 'attendance_leave';

    // Payroll
    if (
      path === '/payroll_dashboard' ||
      path === '/salary_structure' ||
      path === '/salary_structure/create' ||
      path === '/payroll_run' ||
      path === '/payslips' ||
      path.startsWith('/payslips/batch/') ||
      path === '/statutory_compliance' ||
      path === '/full_final'
    ) return 'payroll';

    // Performance
    if (
      path === '/performance_dashboard' ||
      path === '/performance' || // Generic route
      path === '/goal_management' ||
      path === '/review_management' ||
      path === '/performance_outcomes' ||
      path === '/performance_analytics' ||
      path.startsWith('/performance_cycles') ||
      path.startsWith('/performance_cycle_detail/')
    ) return 'performance';

    // Training detail pages – context-sensitive:
    // When navigated from MyTraining (sessionStorage key = 'my_training') highlight My Training,
    // otherwise (admin catalog, learning path detail page, etc.) highlight Training & Development.
    if (
      path.startsWith('/hrms/training/learning-path/') ||
      path.startsWith('/hrms/training/course/')
    ) {
      const ctx = sessionStorage.getItem('trainingNavContext');
      // Note: do NOT removeItem here to support persistence on refresh.
      // It will be overwritten when navigating from a different context (e.g. catalog).
      return ctx === 'my_training' ? 'my_training' : 'training';
    }

    // Training
    if (
      path === '/hrms/training' ||
      path.startsWith('/hrms/training/') ||
      path === '/training_development' ||
      path === '/training' ||
      path === '/training_dashboard' ||
      path === '/training_catalog' ||
      path.startsWith('/training_detail/') ||
      path.startsWith('/modules_lessons') ||
      path === '/quiz_question_bank' ||
      path.startsWith('/quiz_detail/') ||
      path === '/learning_paths' ||
      path.startsWith('/learning_path_detail/') ||
      path === '/assignments_enrollment' ||
      path === '/sessions_scheduling' ||
      path.startsWith('/session_detail/') ||
      path === '/attendance_feedback' ||
      path === '/certificates_credits' ||
      path.startsWith('/certificate_detail/') ||
      path === '/compliance_renewals' ||
      path.startsWith('/compliance_item_detail/') ||
      path === '/reports_analytics' ||
      path === '/training_analytics' ||
      path === '/settings_policies' // Check if this belongs here
    ) return 'training';

    // Exit Management
    if (
      path === '/hrms/exit' ||
      path === '/exit_requests' ||
      path === '/no_dues_clearance' ||
      path === '/relieving_letter' ||
      path.startsWith('/relieving_letter/') ||
      path === '/experience_certificate' ||
      path.startsWith('/experience_certificate/')
    ) return 'exit_management';

    // HR Analytics
    if (
      path === '/hr_analytics_dashboard' ||
      path.startsWith('/hrms/hr-analytics') ||
      path === '/analytics_audit_logs' ||
      path === '/attrition_analysis' ||
      path === '/recruitment_funnel' ||
      path === '/salary_trends' ||
      path === '/compliance_reminders' ||
      path.startsWith('/audit_log_detail/')
    ) return 'analytics_reporting';

    // Compliance & Policy
    if (
      path === '/policy_management' ||
      path.startsWith('/view_policy/') ||
      path.startsWith('/hrms/compliance-policy') ||
      path === '/statutory_compliance' ||
      path === '/policy_library' ||
      path === '/acknowledgments' ||
      path === '/audit_logs'
    ) return 'compliance_policy';

    // Onboarding
    if (
      path === '/hrms/onboarding' ||
      path.startsWith('/hrms/onboarding') ||
      path === '/onboarding' ||
      path === '/document_verification' ||
      path === '/orientation' ||
      path === '/create_orientation' ||
      path.startsWith('/view_orientation')
    ) return 'onboarding';

    // Background Verification
    if (
      path === '/background_verification' ||
      path === '/initiate_background_verification' ||
      path.startsWith('/view_background_verification')
    ) return 'background_verification';
    // User list: Recruitment, Employee DB, Attendance, Payroll, Performance, Training, Exit, HR Analytics.

    // --- CRM MODULES ---

    // Lead Management - Return specific sub-item IDs for proper highlighting
    if (path === '/lead_dashboard') return 'lead_dashboard';
    if (path === '/lead_sources_capture' || path.startsWith('/lead_sources_capture')) return 'lead_sources_capture';
    if (path === '/manual_lead_entry') return 'add_lead';
    if (path === '/lead_list_filters') return 'lead_list_filters';
    if (path === '/lead_assignment' || path === '/lead_assignment_management' || path.startsWith('/assignment_rule_detail/')) return 'lead_assignment';
    if (path === '/lead_activities' || path.startsWith('/log_activity/') || path.startsWith('/activity_detail/')) return 'lead_activities';
    if (path === '/lead_scoring_qualification') return 'lead_scoring_qualification';
    if (path === '/convert_opportunity') return 'convert_opportunity';
    // if (path === '/tender_lead') return 'tender_lead';
    // Lead detail pages should highlight lead_dashboard as fallback
    if (path.startsWith('/lead_detail_360/')) return 'lead_dashboard';

    // Field Sales - Return specific sub-item IDs
    if (path === '/field_sales_dashboard') return 'field_sales_dashboard';
    if (path === '/route_plans') return 'route_plans';
    if (path === '/todays_visits') return 'todays_visits';

    // Opportunity Management - Return specific sub-item IDs
    // if (path === '/opportunity_pipeline' || path.startsWith('/opportunity_detail/')) return 'Opportunity-dashboard';
    // if (path === '/tasks_followups') return 'tasks_followups';
    // if (path === '/close_won_lost') return 'close_won_lost';

    // Quotation & Proposal - Return specific sub-item IDs
    if (path === '/crm/quotation-proposal/dashboard') return 'quotation_dashboard';
    if (path === '/crm/quotation-proposal/create-quotes' || path === '/crm/quotation-proposal/quote-builder') return 'create_quotes';
    // Other quotation pages default to quotation_dashboard
    if (path.startsWith('/crm/quotation-proposal/')) return 'quotation_dashboard';

    // Orders & Logistics decommissioned

    // Customer Relationship - Return specific sub-item IDs
    if (path === '/customer_360' || path.startsWith('/customer_360/')) return 'customer_360';
    if (path === '/tickets_feedback' || path.startsWith('/ticket_detail/')) return 'tickets_feedback';
    // Caller Pop-ups decommissioned
    if (path === '/chatbot_self_service') return 'chatbot_selfservice';

    // Analytics & Reporting - Return specific sub-item IDs
    if (path === '/sales_dashboard') return 'sales_crm_dashboards';
    // if (path === '/quote_order_dashboard') return 'quote_order_tracking';
    // if (path === '/crm/logistics/shipments') return 'logistics_ops_boards'; // NEW Shipway page
    if (path === '/party_financials') return 'party_financials';

    // Target vs Achievement - Return specific sub-item IDs
    if (path === '/targets_dashboard') return 'targets_dashboard';
    if (path === '/created_targets') return 'created_targets';
    if (path === '/schedules') return 'schedules';
    if (path === '/wfh_approval_panel') return 'wfh_approvals';


    // --- SYSTEM ---
    if (path === '/location_management') return 'location_management';
    if (path === '/department_role_mapping') return 'department_management';
    // New split configuration routes
    if (path === '/hrms_configuration') return 'hrms_configuration';
    if (path === '/crm_configuration') return 'crm_configuration';
    if (path === '/website_master') return 'website_master';
    // Legacy route - still maps to hrms_configuration highlight
    if (path === '/configure_management') return 'hrms_configuration';

    // --- SELF SERVICE ---
    if (path === '/my_profile') return 'my_profile';
    if (path === '/my_attendance') return 'my_attendance';
    if (path === '/my_wfh_requests') return 'my_wfh_requests';
    if (path === '/apply_leave') return 'apply_leave';
    if (path === '/my_payslips' || path.startsWith('/my_payslips/')) return 'my_payslips';
    if (path === '/my_training') return 'my_training';
    if (path === '/my_performance') return 'my_performance';
    if (path === '/policies') return 'policies';
    if (path === '/submit_resignation') return 'submit_resignation';
    if (path === '/mydashboard') return 'my_dashboard';

    // HR Back-Office
    if (
      path === '/hrms/back-office' ||
      path.startsWith('/hrms/back-office') ||
      path === '/hr_backoffice_dashboard'
    ) {
      if (params.get('tab') === 'candidateProfiles') return 'candidate_profile_management';
      if (params.get('tab') === 'communicationLogs') return 'offer_communication_logs';
      return 'hr_backoffice';
    }

    return path.replace('/', '');
  }, [location.pathname]);

  // Update active item when location changes - fixed navigation state
  React.useEffect(() => {
    const newActiveItem = getActiveItem();
    setActiveItem(newActiveItem);
  }, [getActiveItem]);

  // Auto-expand parent sections for detail pages
  React.useEffect(() => {
    const path = location.pathname;
    const params = new URLSearchParams(location.search);

    // Auto-expand hrms and analytics_reporting when on audit log pages
    if (path.startsWith("/audit_log_detail/") || path === "/analytics_audit_logs" || path === "/audit_logs") {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        analytics_reporting: true,
      }));
    }

    // Auto-expand hrms and analytics_reporting when on compliance reminder pages
    if (
      path === "/add_compliance_reminder" ||
      path.startsWith("/compliance_reminder_detail/") ||
      path.startsWith("/edit_compliance_reminder/") ||
      path.startsWith("/reminder_notification_settings/")
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        analytics_reporting: true,
      }));
    }

    // Auto-expand crm_sales and lead_management when on any lead management page
    if (
      path.startsWith("/lead_detail_360/") ||
      path.startsWith("/log_activity/") ||
      path.startsWith("/assignment_rule_detail/") ||
      path.startsWith("/activity_detail/") ||
      path === "/manual_lead_entry" ||
      path === "/lead_list_filters" ||
      path === "/lead_activities" ||
      path === "/lead_assignment" ||
      path === "/lead_assignment_management" ||
      path === "/lead_scoring_qualification" ||
      path === "/convert_opportunity" ||


      // path === "/tender_lead" ||
      path === "/lead_dashboard" ||
      path.startsWith("/lead_sources_capture")
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        crm_sales: true,
        lead_management: true,
      }));
    }

    // Auto-expand quotation_proposal when on any quotation page
    if (
      path.startsWith("/crm/quotation-proposal/") ||
      path === "/crm/quotation-proposal/dashboard"
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        crm_sales: true,
        quotation_proposal: true,
      }));
    }

    // Auto-expand field_sales when on field sales pages
    if (
      path === "/field_sales_dashboard" ||
      path === "/route_plans" ||
      path === "/todays_visits"
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        crm_sales: true,
        field_sales: true,
      }));
    }

    // Auto-expand opportunity_pipeline when on opportunity pages
    // if (
    //   path === "/opportunity_pipeline" ||
    //   path.startsWith("/opportunity_detail/") ||
    //   path === "/tasks_followups" ||
    //   path === "/close_won_lost"
    // ) {
    //   setExpandedItems((prev) => ({
    //     ...prev,
    //     crm_sales: true,
    //     opportunity_pipeline: true,
    //   }));
    // }

    // Orders & Logistics decommissioned

    // Auto-expand customer_relationship when on customer pages
    if (
      path === "/customer_360" ||
      path.startsWith("/customer_360/") ||
      path === "/tickets_feedback" ||
      path.startsWith("/ticket_detail/") ||
      path === "/chatbot_self_service"
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        crm_sales: true,
        customer_relationship: true,
      }));
    }

    // Auto-expand crm_analytics_reporting when on CRM analytics pages
    if (
      path === "/sales_dashboard" ||
      // path === "/quote_order_dashboard" ||
      // path === "/crm/logistics/shipments" ||
      path === "/party_financials"
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        crm_sales: true,
        crm_analytics_reporting: true,
      }));
    }

    // Auto-expand target_achievement when on target pages
    if (
      path === "/targets_dashboard" ||
      path === "/created_targets" ||
      path === "/schedules"
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        crm_sales: true,
        target_achievement: true,
      }));
    }

    // Auto-expand hrms and analytics_reporting when on policy management pages
    if (
      path === "/new_policy" ||
      path.startsWith("/view_policy/") ||
      path.startsWith("/edit_policy/") ||
      path.startsWith("/download_policy/") ||
      path.startsWith("/send_policy_reminder/") ||
      path.startsWith("/policy_settings/")
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        analytics_reporting: true,
      }));
    }

    // Auto-expand hrms and compliance_policy when on compliance & policy pages
    if (
      path === "/policy_library" ||
      path.startsWith("/policy_library/") ||
      path === "/acknowledgments"
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        compliance_policy: true,
      }));
    }

    // Auto-expand hrms and recruitment when on edit interview page
    if (path.startsWith("/edit_interview/")) {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        recruitment: true,
      }));
    }

    // Auto-expand hrms and recruitment when on offer pages
    if (path.startsWith("/view_offer/") || path.startsWith("/edit_offer/")) {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        recruitment: true,
      }));
    }

    // Auto-expand hrms and recruitment when on candidate pages
    if (
      path === "/add_candidate" ||
      path.startsWith("/edit_candidate/") ||
      path.startsWith("/view_candidate/")
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        recruitment: true,
      }));
    }

    // Auto-expand hrms and recruitment when on recruitment sub-pages
    if (
      path === '/create-job' ||
      path.startsWith('/edit-job/') ||
      path === '/job_posts' ||
      path === '/candidate_pipeline' ||
      path === '/interview_scheduling' ||
      path.startsWith('/edit_interview/') ||
      path === '/recruitment' ||
      path === '/hrms/recruitment'
    ) {
      setExpandedItems(prev => ({
        ...prev,
        hrms: true,
        recruitment: true
      }));
    }

    // Auto-expand hrms and recruitment when on recruitment analytics pages
    if (path === '/recruitment_analytics') {
      setExpandedItems(prev => ({
        ...prev,
        hrms: true,
        recruitment: true,
      }));
    }

    // Auto-expand hrms + correct sub-section when on communication pages
    if (
      path === '/new_communication' ||
      path.startsWith('/edit_communication/') ||
      path.startsWith('/reply_communication/') ||
      path.startsWith('/forward_communication/') ||
      path.startsWith('/view_communication/')
    ) {
      const ctx = sessionStorage.getItem('communicationNavContext');
      // Note: do NOT removeItem here — getActiveItem() will consume it
      if (ctx === 'hr_backoffice') {
        setExpandedItems(prev => ({
          ...prev,
          hrms: true,
          hr_backoffice: true,
        }));
      } else {
        setExpandedItems(prev => ({
          ...prev,
          hrms: true,
          recruitment: true,
        }));
      }
    }

    // Handle candidate_profile_management - expand recruitment menu
    if (path === '/candidate_profile_management' || (path === '/hrms/back-office' && params.get('tab') === 'candidateProfiles')) {
      setExpandedItems(prev => ({
        ...prev,
        hrms: true,
        recruitment: true,
      }));
    }

    // Handle offer_communication_logs - expand based on navigation context
    if (path === '/offer_communication_logs' ||
      path === '/new_communication' ||
      path.startsWith('/edit_communication/') ||
      path.startsWith('/reply_communication/') ||
      path.startsWith('/forward_communication/') ||
      path.startsWith('/view_communication/')) {
      setExpandedItems(prev => {
        const navContext = sessionStorage.getItem('navContext');
        // Clear the context after using it
        sessionStorage.removeItem('navContext');

        // If navigation context indicates hr_backoffice, expand that
        if (navContext === 'hr_backoffice') {
          return {
            ...prev,
            hrms: true,
            hr_backoffice: true,
          };
        }
        // If navigation context indicates recruitment, expand that
        if (navContext === 'recruitment') {
          return {
            ...prev,
            hrms: true,
            recruitment: true,
          };
        }
        // Fallback: if hr_backoffice is already expanded, keep it
        if (prev.hr_backoffice) {
          return {
            ...prev,
            hrms: true,
            hr_backoffice: true,
          };
        }
        // Otherwise, expand recruitment (default behavior)
        return {
          ...prev,
          hrms: true,
          recruitment: true,
        };
      });
    }



    // Auto-expand hrms and employee_database when on employee pages
    if (
      path === "/employee_database" ||
      path === "/employee_list" ||
      path === "/add_employee" ||
      path.startsWith("/employee_profile/") ||
      path.startsWith("/edit_employee/")
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        employee_database: true,
      }));
    }

    // Auto-expand employee_self_service when on employee self-service pages
    if (
      path === "/mydashboard" ||
      path === "/my_profile" ||
      path === "/my_attendance" ||
      path === "/my_wfh_requests" ||
      path === "/apply_leave" ||
      path === "/my_payslips" ||
      path === "/my_training" ||
      path === "/my_performance" ||
      path === "/policies" ||
      path === "/submit_resignation" ||
      // Detail pages reached from My Training
      ((path.startsWith("/hrms/training/learning-path/") ||
        path.startsWith("/hrms/training/course/")) &&
        sessionStorage.getItem('trainingNavContext') === 'my_training')
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        employee_self_service: true,
      }));
    }

    // Auto-expand hrms and training when on training development pages
    if (
      path === "/training_development" ||
      path === "/training_dashboard" ||
      path === "/training_catalog" ||
      path === "/modules_lessons" ||
      path.startsWith("/modules_lessons/") ||
      path === "/quiz_question_bank" ||
      path === "/learning_paths" ||
      path === "/assignments_enrollment" ||
      path === "/sessions_scheduling" ||
      path === "/attendance_feedback" ||
      path === "/certificates_credits" ||
      path === "/compliance_renewals" ||
      path === "/reports_analytics" ||
      path === "/settings_policies" ||
      // Detail pages reached from Training Catalog (not My Training)
      ((path.startsWith("/hrms/training/learning-path/") ||
        path.startsWith("/hrms/training/course/")) &&
        sessionStorage.getItem('trainingNavContext') !== 'my_training')
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        training: true,
      }));
    }

    if (
      path === "/attendance_leave" ||
      path === "/attendance_dashboard" ||
      path === "/attendance_logs" ||
      path === "/timesheet" ||
      path === "/regularization" ||
      path === "/wfh_approval_panel" ||
      path === "/leave_requests" ||
      path === "/leave_policies" ||
      path === "/holiday_calendar" ||
      path === "/shift_roster" ||
      path === "/attendance_records" ||
      path === "/attendance_analytics"
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        attendance_leave: true,
      }));
    }

    // Auto-expand hrms and performance when on performance detail pages
    if (
      path.startsWith("/performance_cycle_detail/") ||
      path.startsWith("/calibration_detail/")
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        performance: true,
      }));
    }

    // Auto-expand hrms and payroll when on payroll pages
    // Note: statutory_compliance is handled separately below to check context
    if (
      path === "/payroll_dashboard" ||
      path === "/salary_structure" ||
      path === "/payroll_run" ||
      path === "/payslips" ||
      path === "/full_final"
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        hrms: true,
        payroll: true,
      }));
    }

    // Handle statutory_compliance - check if coming from compliance_policy or payroll
    if (path === "/statutory_compliance") {
      setExpandedItems((prev) => {
        // If compliance_policy is already expanded, expand compliance_policy (NOT payroll)
        if (prev.compliance_policy) {
          return {
            ...prev,
            hrms: true,
            compliance_policy: true,
          };
        }
        // Otherwise, expand payroll (only when accessed from payroll menu or direct navigation)
        return {
          ...prev,
          hrms: true,
          payroll: true,
        };
      });
    }

    // Auto-expand system_management + system_hrms for HRMS system pages
    if (
      path === "/location_management" ||
      path === "/department_role_mapping" ||
      path === "/hrms_configuration" ||
      path === "/configure_management" // legacy route → also expands HRMS group
    ) {
      setExpandedItems((prev) => ({
        ...prev,
        system_management: true,
        system_hrms: true,
      }));
    }

    // Auto-expand system_management + system_crm for CRM system pages
    if (path === "/crm_configuration" || path === "/website_master") {
      setExpandedItems((prev) => ({
        ...prev,
        system_management: true,
        system_crm: true,
      }));
    }
  }, [location.pathname, location.search]);

  const handleNavigate = (item) => {
    // Define route mappings for complex navigation items
    const routeMap = {
      'ai_copilot': '/ai-copilot',
      'dashboard': '/dashboard',
      'hrms': '/hrms',
      'audit_logs': '/hrms?view=audit_logs', // Route to HRMSContainer for compliance policy audit logs
      'crm_sales': '/crm_sales',
      'lead_management': '/lead_dashboard',
      // 'opportunity_pipeline': '/Opportunity-dashboard',
      'quotation_proposal': '/crm/quotation-proposal/dashboard',
      'quotation_dashboard': '/crm/quotation-proposal/dashboard',
      'create_quotes': '/crm/quotation-proposal/create-quotes',
      'quote_builder': '/crm/quotation-proposal/quote-builder',
      'send_track': '/crm/quotation-proposal/send-track',
      'version_history': '/crm/quotation-proposal/version-history',
      'lead_dashboard': '/lead_dashboard',
      'add_lead': '/manual_lead_entry',
      'manual_lead_entry': '/manual_lead_entry',
      'lead_sources_capture': '/lead_sources_capture',
      'lead_list_filters': '/lead_list_filters',
      'lead_detail_360': '/lead_detail_360',
      'log_activity': '/log_activity',
      'lead_activities': '/lead_activities',
      'lead_assignment': '/lead_assignment',
      'lead_scoring_qualification': '/lead_scoring_qualification',
      'convert_opportunity': '/convert_opportunity',


      // 'tender_lead': '/tender_lead',
      'field_sales': '/field_sales_dashboard',
      'field_sales_dashboard': '/field_sales_dashboard',
      'route_plans': '/route_plans',
      'todays_visits': '/todays_visits',
      // TO BE DELETED - Commented out route mappings
      // 'checkin_checkout': '/checkin_checkout',
      // 'visit_log': '/visit_log',
      // 'geo_map': '/geo_map',
      // 'sync_settings': '/sync_settings',
      // 'Opportunity-dashboard': '/opportunity_pipeline',
      // 'pipeline_dashboard': '/Opportunity-dashboard',
      // // 'opportunity_kanban': '/opportunity_kanban',
      // 'opportunity_detail': '/opportunity_detail',
      // 'tasks_followups': '/tasks_followups',
      // 'close_won_lost': '/close_won_lost',
      // 'forecasts_targets': '/forecasts_targets',
      // 'pipeline_settings': '/pipeline_settings',
      // Orders & Logistics routes decommissioned
      // Customer Relationship routes
      'customer_360': '/customer_360',
      'tickets_feedback': '/tickets_feedback',
      // 'caller_popups': '/caller_popups',
      'chatbot_selfservice': '/chatbot_self_service',
      // Analytics & Reporting routes
      'sales_crm_dashboards': '/sales_dashboard',
      // 'quote_order_tracking': '/quote_order_dashboard',
      // 'logistics_ops_boards': '/crm/logistics/shipments',
      'party_financials': '/party_financials',
      // Target vs Achievement routes
      'targets_dashboard': '/targets_dashboard',
      'created_targets': '/created_targets',
      'schedules': '/schedules',
      'hr_backoffice': '/hrms/back-office',
      'hr_backoffice_dashboard': '/hrms/back-office',
      'candidate_profile_management': '/hrms/back-office?tab=candidateProfiles',
      'analytics_reporting': '/hrms/hr-analytics',
      'hr_analytics_dashboard': '/hrms/hr-analytics',
      'analytics_audit_logs': '/analytics_audit_logs',
      'add_compliance_reminder': '/add_compliance_reminder',
      'compliance_reminder_detail': '/compliance_reminder_detail',
      'edit_compliance_reminder': '/edit_compliance_reminder',
      'reminder_notification_settings': '/reminder_notification_settings',
      'audit_log_detail': '/audit_log_detail',
      'offer_communication_logs': '/hrms/back-office?tab=communicationLogs',
      'hrms_analytics_dashboard': '/hrms_analytics_dashboard',
      'employee_analytics': '/employee_analytics',
      'employee_database': '/employee_list',
      'employee_list': '/employee_list',
      'attendance_leave': '/attendance_dashboard',
      'wfh_approvals': '/attendance_dashboard?tab=wfhApprovals',
      'attendance_dashboard': '/attendance_dashboard',
      'attendance_analytics': '/attendance_analytics',
      'add_employee': '/add_employee',
      'employee_profile': '/employee_profile',
      'edit_employee': '/edit_employee',
      'attendance_analytics': '/attendance_analytics',
      'payroll_analytics': '/payroll_analytics',
      'performance_analytics': '/performance_analytics',
      'recruitment': '/hrms/recruitment', // Canonical route for recruitment
      'onboarding': '/hrms/onboarding', // Canonical route for onboarding
      'recruitment_analytics': '/recruitment_analytics',
      'performance': '/performance_dashboard',
      'performance_dashboard': '/performance_dashboard',
      'performance_analytics': '/performance_analytics',
      'training_analytics': '/training_analytics',
      'compliance_policy': '/hrms/compliance-policy',
      'statutory_compliance': '/hrms/compliance-policy?tab=statutoryCompliance',
      'policy_library': '/hrms/compliance-policy?tab=policyLibrary',
      'acknowledgments': '/hrms/compliance-policy?tab=acknowledgments',
      'audit_logs': '/hrms/compliance-policy?tab=auditLogs',
      'exit_management': '/hrms/exit',
      'exit_requests': '/hrms/exit?tab=exitRequests',
      'hr_reports': '/hr_reports',
      'my_dashboard': '/mydashboard',
      'employee_self_service': '/mydashboard',
      'my_profile': '/my_profile',
      'my_attendance': '/my_attendance',
      'my_wfh_requests': '/my_wfh_requests',
      'apply_leave': '/apply_leave',
      'my_payslips': '/my_payslips',
      'my_training': '/my_training',
      'my_performance': '/my_performance',
      'policies': '/policies',
      'background_verification': '/hrms/onboarding?tab=backgroundVerification',
      'submit_resignation': '/submit_resignation',
      'profile': '/profile',
      'logout': '/logout',
      'new_policy': '/new_policy',
      'view_policy': '/view_policy',
      'edit_policy': '/edit_policy',
      'download_policy': '/download_policy',
      'send_policy_reminder': '/send_policy_reminder',
      'policy_settings': '/policy_settings',
      'location_management': '/location_management',
      'experience_management': '/experience_management',
      'department_management': '/department_role_mapping',
      // New split configuration routes
      'hrms_configuration': '/hrms_configuration',
      'crm_configuration': '/crm_configuration',
      'website_master': '/website_master',
      // Legacy route kept for backwards compatibility
      'configure_management': '/hrms_configuration',
      'view_offer': '/view_offer',
      'edit_offer': '/edit_offer',
      'add_candidate': '/add_candidate',
      'edit_candidate': '/edit_candidate',
      'view_candidate': '/view_candidate',
      'new_communication': '/new_communication',
      'edit_communication': '/edit_communication',
      'reply_communication': '/reply_communication',
      'forward_communication': '/forward_communication',
      'view_communication': '/view_communication',

      'training': '/training_development',
      'training_development': '/training_development',
      'training_dashboard': '/training_dashboard',
      'training_catalog': '/training_catalog',
      'modules_lessons': '/modules_lessons',
      'quiz_question_bank': '/quiz_question_bank',
      'learning_paths': '/learning_paths',
      'assignments_enrollment': '/assignments_enrollment',
      'sessions_scheduling': '/sessions_scheduling',
      'attendance_feedback': '/attendance_feedback',
      'certificates_credits': '/certificates_credits',
      'compliance_renewals': '/compliance_renewals',
      'reports_analytics': '/reports_analytics',
      'settings_policies': '/settings_policies',
      'performance_cycle_detail': '/performance_cycle_detail',
      'calibration_detail': '/calibration_detail',
      // Payroll routes
      'payroll': '/payroll_dashboard',
      'payroll_dashboard': '/payroll_dashboard',
      'salary_structure': '/salary_structure',
      'payroll_run': '/payroll_run',
      'payslips': '/payslips',
      'statutory_compliance': '/statutory_compliance',
      'full_final': '/hrms/exit?tab=fullAndFinalSettlement'
    };

    const expandOnlyItems = [
      "crm_sales",
      "system_management",
      "system_hrms",
      "system_crm",
      "employee_self_service",
      "crm_settings",
      "system_access",
    ];

    if (expandOnlyItems.includes(item)) {
      return;
    }

    if (routeMap[item]) {
      navigate(routeMap[item]);
    } else {
      navigate(`/${item}`);
    }
  };

  const handleLogout = async () => {
    try {

      await logout();

      // Force navigation to login page
      navigate("/login", { replace: true });
    } catch (error) {

      // Even if logout fails, navigate to login
      navigate("/login", { replace: true });
    }
  };

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleToggleExpanded = (itemId) => {
    setExpandedItems((prev) => {
      const newState = {
        ...prev,
        [itemId]: !prev[itemId],
      };
      // Persist to localStorage
      try {
        localStorage.setItem('sidebarExpandedItems', JSON.stringify(newState));
      } catch (error) {

      }
      return newState;
    });
  };

  // Persist expandedItems to localStorage whenever it changes
  React.useEffect(() => {
    try {
      localStorage.setItem('sidebarExpandedItems', JSON.stringify(expandedItems));
    } catch (error) {

    }
  }, [expandedItems]);

  const notifications = [
    {
      id: 1,
      title: "New Leave Request",
      message: "John Doe has submitted a leave request",
      time: "2 hours ago",
      read: false,
    },
    {
      id: 2,
      title: "Performance Review Due",
      message: "Quarterly performance reviews are due next week",
      time: "1 day ago",
      read: true,
    },
  ];

  return (
    <div
      className="h-screen flex overflow-hidden bg-slate-50"
    >
      <div
        className={`transition-all duration-300 flex-shrink-0 ${
          isSidebarCollapsed ? "w-16" : "w-[265px] lg:w-[270px] xl:w-[275px]"
        } overflow-hidden`}
      >
        <Sidebar
          userRole={user?.role}
          user={user}
          onNavigate={handleNavigate}
          activeItem={activeItem}
          onLogout={handleLogout}
          isCollapsed={isSidebarCollapsed}
          expandedItems={expandedItems}
          onToggleExpanded={handleToggleExpanded}
          searchTerm={globalSearchTerm}
        />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          onToggleSidebar={handleToggleSidebar}
          isSidebarCollapsed={isSidebarCollapsed}
          user={user}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
          notifications={notifications}
          searchTerm={globalSearchTerm}
          onSearchChange={setGlobalSearchTerm}
        />
        <main ref={mainContentRef} className={`flex-1 overflow-y-auto relative ${location.pathname === '/ai-copilot' ? 'bg-transparent p-0' : 'bg-gray-50 p-6'}`}>
          {children}
        </main>
      </div>
    </div>
  );
};

// App Routes Component
const AppRoutes = () => {
  const { isAuthenticated, user } = useAuth();

  return (
    <React.Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <LoginRedirectRouter /> : <Login />
          }
        />
        <Route
          path="/policy_library/acknowledgment/:id"
          element={<PublicPolicyAcknowledgeRedirect />}
        />
        <Route
          path="/ai-copilot"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AICopilotPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DashboardRouter />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/system_access"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <MainLayout>
                <PlaceholderPage
                  title="System & Access"
                  description="Manage users, roles, MFA policies, and system security settings"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users_roles"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <MainLayout>
                <PlaceholderPage
                  title="Users & Roles (RBAC)"
                  description="Manage user accounts and role-based access control"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/location_management"
          element={
            <ProtectedRoute requiredRole={['super_admin', 'hr_manager']}>
              <MainLayout>
                <LocationManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/global_leave_configuration"
          element={
            <ProtectedRoute permission="attendance">
              <MainLayout>
                <GlobalLeaveConfiguration />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/configure_management"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ConfigureManagement module="hrms" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hrms_configuration"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ConfigureManagement module="hrms" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm_configuration"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ConfigureManagement module="crm" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/website_master"
          element={
            <ProtectedRoute>
              <MainLayout>
                <WebsiteMaster />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/mfa_policy"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <MainLayout>
                <PlaceholderPage
                  title="MFA Policy"
                  description="Configure multi-factor authentication policies"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/session_settings"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <MainLayout>
                <PlaceholderPage
                  title="Session Settings"
                  description="Configure session timeout and security settings"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit_logs"
          element={
            <ProtectedRoute requiredPermission="reports" requiredAction="read">
              <Navigate to="/analytics_audit_logs" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit_log_detail/:id"
          element={
            <ProtectedRoute requiredPermission="reports" requiredAction="read">
              <MainLayout>
                <AuditLogDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hrms"
          element={
            <ProtectedRoute>
              <MainLayout>
                <HRMSRouteHandler />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hrms/recruitment"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RecruitmentModule initialTab="jobs" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RecruitmentModule initialTab="jobs" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/job_posts"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RecruitmentModule initialTab="jobs" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-job"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CreateJob />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-job/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ViewJob />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-job/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CreateJob />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/job-application/:jobId" element={<JobApplication />} />
        <Route
          path="/candidate_pipeline"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RecruitmentModule initialTab="pipeline" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/interview_scheduling"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RecruitmentModule initialTab="interviews" />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/view_offer/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ViewOffer />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/view_interview/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ViewInterview />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit_offer/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EditOffer />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add_candidate"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AddCandidate />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit_candidate/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AddCandidate />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/view_candidate/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ViewCandidate />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/offer_management"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RecruitmentModule initialTab="offers" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment_analytics"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RecruitmentModule initialTab="analytics" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hrms/onboarding"
          element={
            <ProtectedRoute requiredPermission={["onboarding", "self_service"]}>
              <MainLayout>
                <OnboardingModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding"
          element={<Navigate to="/hrms/onboarding?tab=documentVerification" replace />}
        />
        <Route
          path="/document_verification"
          element={<Navigate to="/hrms/onboarding?tab=documentVerification" replace />}
        />

        <Route
          path="/background_verification"
          element={<Navigate to="/hrms/onboarding?tab=backgroundVerification" replace />}
        />
        <Route
          path="/initiate_background_verification"
          element={
            <ProtectedRoute
              requiredRole={["super_admin", "hr_manager", "hr_executive"]}
            >
              <MainLayout>
                <InitiateBackgroundVerification />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/view_background_verification/:id"
          element={
            <ProtectedRoute requiredPermission={["onboarding", "self_service"]} requiredAction="read">
              <MainLayout>
                <ViewBackgroundVerification />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit_background_verification/:id"
          element={
            <ProtectedRoute requiredPermission="onboarding" requiredAction="update">
              <MainLayout>
                <InitiateBackgroundVerification />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/send_verification_reminder/:id"
          element={<LegacyOnboardingRedirect tab="backgroundVerification" action="viewBackgroundVerification" />}
        />
        <Route
          path="/refresh_verification_status/:id"
          element={<LegacyOnboardingRedirect tab="backgroundVerification" action="viewBackgroundVerification" />}
        />
        <Route
          path="/orientation"
          element={<Navigate to="/hrms/onboarding?tab=orientation" replace />}
        />
        <Route
          path="/create_orientation"
          element={
            <ProtectedRoute
              requiredRole={["super_admin", "hr_manager", "hr_executive"]}
            >
              <MainLayout>
                <CreateOrientation />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/create_orientation/:id"
          element={
            <ProtectedRoute
              requiredRole={["super_admin", "hr_manager", "hr_executive"]}
            >
              <MainLayout>
                <CreateOrientation />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/view_orientation/:id"
          element={
            <ProtectedRoute
              requiredPermission="onboarding"
              requiredAction="read"
            >
              <MainLayout>
                <ViewOrientation />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/send_orientation_invites/:id"
          element={
            <ProtectedRoute
              requiredRole={["super_admin", "hr_manager", "hr_executive"]}
            >
              <MainLayout>
                <SendOrientationInvites />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/orientation/invite/:inviteId"
          element={<OrientationInviteResponse />}
        />
        <Route
          path="/orientation/invite/:inviteId/:action"
          element={<OrientationInviteResponse />}
        />
        <Route
          path="/clone_orientation/:id"
          element={
            <ProtectedRoute
              requiredRole={["super_admin", "hr_manager", "hr_executive"]}
            >
              <MainLayout>
                <CloneOrientation />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employee_list"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EmployeeModule initialTab="employeeList" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add_employee"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AddEmployee />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee_profile"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EmployeeProfile />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee_profile/:employeeId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EmployeeProfile />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit_employee/:employeeId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EditEmployee />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/transfers"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EmployeeModule initialTab="transfers" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/document_manager/:employeeId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DocumentManager />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/department_role_mapping"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DepartmentRoleMapping />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* Note: DepartmentRoleMapping is also rendered inside HRMSContainer at case 'department_role_mapping'.
          The standalone route above is the canonical one; HRMSContainer renders it when navigated via internal HRMS nav.
          To avoid having two instances mounted simultaneously (which causes double API calls),
          any navigation to this page should use only ONE path. */}
        <Route
          path="/transfer_role_change/:employeeId?"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TransferRoleChange />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bulk_upload"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EmployeeModule initialTab="bulkUpload" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* Training & Development - Canonical Route */}
        <Route
          path="/hrms/training"
          element={
            <ProtectedRoute requiredPermission="training" requiredAction="read">
              <MainLayout>
                <TrainingModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Legacy Training Routes (Aliases) */}
        <Route path="/training" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/training_development" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/training_dashboard" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/training_catalog" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/modules_lessons" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/modules_lessons/:courseId" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/quiz_question_bank" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/learning_paths" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/assignments_enrollment" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/sessions_scheduling" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/sessions" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/attendance_feedback" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/attendance_management" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/certificates_credits" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/certificates_transcripts" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/certificates" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/compliance_renewals" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/compliance_expirations" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/reports_analytics" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/overview_analytics" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/training_analytics" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/settings_policies" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />

        {/* Course Detail - Full Page View */}
        <Route
          path="/hrms/training/course/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CourseDetailPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Learning Path Detail - Full Page View */}
        <Route
          path="/hrms/training/learning-path/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LearningPathDetailPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Assignment Detail - Full Page View */}
        <Route
          path="/hrms/training/assignment/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AssignmentDetailsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Training Detail/Action Routes */}
        <Route path="/training_detail/:id" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/training_detail/:id/edit" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/quiz_detail/:id" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/quiz_detail/:id/edit" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/learning_path_detail/:id" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/learning_path_detail/:id/edit" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/session_detail/:id" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/certificate_detail/:id" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route path="/compliance_item_detail/:id" element={<ProtectedRoute requiredPermission="training"><MainLayout><TrainingModule /></MainLayout></ProtectedRoute>} />
        <Route
          path="/attendance_leave"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AttendanceLeaveModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hrms/attendance-leave"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AttendanceLeaveModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/attendance_dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AttendanceLeaveModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/attendance_kpi_dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AttendanceLeaveModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/attendance_logs"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AttendanceLeaveModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/timesheet"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Timesheet />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/regularization"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AttendanceLeaveModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave_requests"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AttendanceLeaveModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave_policies"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AttendanceLeaveModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/holiday_calendar"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AttendanceLeaveModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shift_roster"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AttendanceLeaveModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PayrollModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll_dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PayrollModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salary_structure"
          element={
            <ProtectedRoute requiredPermission="payroll" requiredAction="read">
              <MainLayout>
                <PayrollModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/salary_structure/create"
          element={
            <ProtectedRoute requiredPermission="payroll" requiredAction="create">
              <MainLayout>
                <PayrollModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll_run"
          element={
            <ProtectedRoute requiredPermission="payroll" requiredAction="read">
              <MainLayout>
                <PayrollModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll_run/:runId"
          element={
            <ProtectedRoute requiredPermission="payroll" requiredAction="read">
              <MainLayout>
                <PayrollModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payslips"
          element={
            <ProtectedRoute requiredPermission="payroll" requiredAction="read">
              <MainLayout>
                <PayrollModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payslips/batch/:batchId"
          element={
            <ProtectedRoute requiredPermission="payroll" requiredAction="read">
              <MainLayout>
                <PayrollModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/payslips/:id"
          element={
            <ProtectedRoute requiredPermission="payroll" requiredAction="read">
              <MainLayout>
                <PayrollModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/distribution_logs"
          element={
            <ProtectedRoute requiredPermission="payroll" requiredAction="read">
              <MainLayout>
                <PayrollModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* Statutory Compliance Legacy Redirect */}
        <Route
          path="/statutory_compliance"
          element={
            <ProtectedRoute requiredPermission="compliance_policy">
              <MainLayout>
                <CompliancePolicyModule initialTab="statutoryCompliance" />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/full_final"
          element={<Navigate to="/hrms/exit?tab=fullAndFinalSettlement" replace />}
        />
        <Route
          path="/hrms/performance"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance_dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance_cycles"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance_cycle_detail/:cycleId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance_cycle_detail/:cycleId/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/goal_setting"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/goal_detail/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviews"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/outcomes"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance_calibration"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance_analytics"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PerformanceModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lead_dashboard"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <LeadDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lead_sources_capture"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <LeadSourcesCapture />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lead_list_filters"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <LeadListFilters />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lead_detail_360"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <Navigate to="/lead_list_filters" replace />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lead_detail_360/:id"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <LeadDetail360 />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/log_activity/:id"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <LogActivity />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lead_activities"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <LeadActivities />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity_detail/:id"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <ActivityDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lead_assignment"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <LeadAssignment />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignment_rule_detail/:id"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <AssignmentRuleDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/scoring_rule/:id"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <ScoringRuleDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/qualification_criteria/:id"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <QualificationCriteriaDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lead_scoring_qualification"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <LeadScoringQualification />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/convert_opportunity"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <ConvertOpportunity />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* <Route
          path="/tender_lead"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <TenderLead />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenders/new"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <TenderForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenders/:id/edit"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <TenderForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenders/:id"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <TenderDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/field_sales_dashboard"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <FieldSalesDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/route_plans"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <RoutePlans />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/todays_visits"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <RoutePlans isTodayOnly={true} />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/route_plan_details/:id"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <RoutePlanDetails />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* TO BE DELETED - Commented out routes */}
        {/* <Route
        path="/checkin_checkout"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CheckinCheckout />
            </MainLayout>
          </ProtectedRoute>
        }
      /> */}
        {/* <Route
        path="/visit_log"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RoutePlans isTodayOnly={true} />
            </MainLayout>
          </ProtectedRoute>
        }
      /> */}
        {/* <Route
        path="/geo_map"
        element={
          <ProtectedRoute requiredRole={["super_admin", "hr_manager"]}>
            <MainLayout>
              <GeoMap />
            </MainLayout>
          </ProtectedRoute>
        }
      /> */}
        {/* <Route
        path="/sync_settings"
        element={
          <ProtectedRoute requiredRole={["super_admin"]}>
            <MainLayout>
              <SyncSettings />
            </MainLayout>
          </ProtectedRoute>
        }
      /> */}
        <Route
          path="/hrms/back-office"
          element={
            <ProtectedRoute requiredRole={["super_admin", "hr_manager"]}>
              <MainLayout>
                <BackOfficeModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hr_backoffice_dashboard"
          element={<Navigate to="/hrms/back-office?tab=dashboard" replace />}
        />
        <Route
          path="/candidate_profile_management"
          element={<Navigate to="/hrms/back-office?tab=candidateProfiles" replace />}
        />
        <Route
          path="/offer_communication_logs"
          element={<Navigate to="/hrms/back-office?tab=communicationLogs" replace />}
        />
        <Route
          path="/new_communication"
          element={
            <ProtectedRoute
              requiredRole={["super_admin", "hr_manager", "recruiter"]}
            >
              <MainLayout>
                <NewCommunication />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit_communication/:id"
          element={
            <ProtectedRoute
              requiredRole={["super_admin", "hr_manager", "recruiter"]}
            >
              <MainLayout>
                <EditCommunication />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reply_communication/:id"
          element={
            <ProtectedRoute
              requiredRole={[
                "super_admin",
                "hr_manager",
                "recruiter",
                "hr_executive",
              ]}
            >
              <MainLayout>
                <ReplyCommunication />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/forward_communication/:id"
          element={
            <ProtectedRoute
              requiredRole={[
                "super_admin",
                "hr_manager",
                "recruiter",
                "hr_executive",
              ]}
            >
              <MainLayout>
                <ForwardCommunication />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/view_communication/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ViewCommunication />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hrms_analytics_dashboard"
          element={
            <ProtectedRoute requiredRole={["super_admin", "hr_manager"]}>
              <MainLayout>
                <PlaceholderPage
                  title="HRMS Analytics Dashboard"
                  description="Comprehensive HR analytics and insights"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee_analytics"
          element={
            <ProtectedRoute requiredRole={["super_admin", "hr_manager"]}>
              <MainLayout>
                <PlaceholderPage
                  title="Employee Analytics"
                  description="Employee data analytics and insights"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/attendance_analytics"
          element={
            <ProtectedRoute requiredRole={["super_admin", "hr_manager"]}>
              <MainLayout>
                <PlaceholderPage
                  title="Attendance Analytics"
                  description="Attendance patterns and analytics"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll_analytics"
          element={
            <ProtectedRoute requiredRole={["super_admin", "hr_manager"]}>
              <MainLayout>
                <PlaceholderPage
                  title="Payroll Analytics"
                  description="Payroll data analytics and insights"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/performance_analytics"
          element={
            <ProtectedRoute requiredRole={["super_admin", "hr_manager"]}>
              <MainLayout>
                <PlaceholderPage
                  title="Performance Analytics"
                  description="Performance analytics and insights dashboard"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recruitment_analytics"
          element={
            <ProtectedRoute requiredRole={["super_admin", "hr_manager"]}>
              <MainLayout>
                <RecruitmentAnalytics />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/training_analytics"
          element={
            <ProtectedRoute requiredPermission="training" requiredAction="read">
              <MainLayout>
                <TrainingModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hr_reports"
          element={
            <ProtectedRoute requiredRole={["super_admin", "hr_manager"]}>
              <MainLayout>
                <PlaceholderPage
                  title="HR Reports"
                  description="HR reports and documentation"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={<Navigate to="/my_profile" replace />}
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Settings />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/logout"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Logout"
                  description="You have been logged out successfully"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/manual_lead_entry"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <ManualLeadEntry />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* <Route
        path="/Opportunity-dashboard"
        element={
          <ProtectedRoute requiredRole={["super_admin", "hr_manager"]}>
            <MainLayout>
              <PipelineDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      /> */}
        {/* <Route
        path="/opportunity_kanban"
        element={
          <ProtectedRoute requiredRole={["super_admin", "hr_manager"]}>
            <MainLayout>
              <OpportunityKanban />
            </MainLayout>
          </ProtectedRoute>
        }
      /> */}
        {/* OpportunityDetail routes removed - component not in use */}
        {/* <Route
          path="/tasks_followups"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <TasksFollowups />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/close_won_lost"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <CloseWonLost />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/forecasts_targets"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <ForecastsTargets />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pipeline_settings"
          element={
            <ProtectedRoute requiredRole={["super_admin"]}>
              <MainLayout>
                <PipelineSettings />
              </MainLayout>
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/crm/quotation-proposal/dashboard"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <DoiDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/quotation-proposal/create-quotes"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <CreateQuotesList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/quotation-proposal/doi-view"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <DoiView />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/quotation-proposal/quote-builder"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <QuoteBuilder />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/quotation-proposal/quote-builder/:opportunityId"
          element={
            <ProtectedRoute requiredRole={["super_admin", "sales_manager", "sales_executive", "dispatch_manager"]}>
              <MainLayout>
                <QuoteBuilder />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/quotation-proposal/send-track"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SendTrack />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/quotation-proposal/send-track/:quoteId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SendTrack />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/quotation-proposal/version-history"
          element={
            <ProtectedRoute>
              <MainLayout>
                <VersionHistory />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/quotation-proposal/version-history/:quoteId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <VersionHistory />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/compliance"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PolicyLibrary />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hrms/exit"
          element={
            <ProtectedRoute requiredPermission="exit_management" requiredAction="read">
              <MainLayout>
                <ExitModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route path="/exit_requests" element={<ProtectedRoute requiredPermission="exit_management"><MainLayout><ExitModule /></MainLayout></ProtectedRoute>} />
        <Route path="/no_dues" element={<ProtectedRoute requiredPermission="exit_management"><MainLayout><ExitModule /></MainLayout></ProtectedRoute>} />
        <Route path="/relieving_letter" element={<ProtectedRoute requiredPermission="exit_management"><MainLayout><ExitModule /></MainLayout></ProtectedRoute>} />
        <Route path="/relieving_letter/:id" element={<ProtectedRoute requiredPermission="exit_management"><MainLayout><RelievingLetterDetail /></MainLayout></ProtectedRoute>} />
        <Route path="/experience_certificate" element={<ProtectedRoute requiredPermission="exit_management"><MainLayout><ExitModule /></MainLayout></ProtectedRoute>} />
        <Route path="/experience_certificate/:id" element={<ProtectedRoute requiredPermission="exit_management"><MainLayout><ExitModule /></MainLayout></ProtectedRoute>} />

        {/* Compliance & Policy - Canonical Route */}
        <Route
          path="/hrms/compliance-policy"
          element={
            <ProtectedRoute requiredPermission="compliance_policy" requiredAction="read">
              <MainLayout>
                <CompliancePolicyModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Legacy Compliance Routes (Aliases) */}
        <Route path="/policy_library" element={<ProtectedRoute requiredPermission="compliance_policy"><MainLayout><CompliancePolicyModule initialTab="policyLibrary" /></MainLayout></ProtectedRoute>} />
        <Route path="/acknowledgments" element={<ProtectedRoute requiredPermission="compliance_policy"><MainLayout><CompliancePolicyModule initialTab="acknowledgments" /></MainLayout></ProtectedRoute>} />
        <Route path="/audit_logs" element={<ProtectedRoute requiredPermission="compliance_policy"><MainLayout><CompliancePolicyModule initialTab="auditLogs" /></MainLayout></ProtectedRoute>} />

        {/* HR Analytics - Canonical Route */}
        <Route
          path="/hrms/hr-analytics"
          element={
            <ProtectedRoute requiredPermission="reports">
              <MainLayout>
                <HRAnalyticsModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Legacy Analytics Routes (Aliases) */}
        <Route
          path="/hr_analytics_dashboard"
          element={<Navigate to="/hrms/hr-analytics?tab=hrAnalyticsDashboard" replace />}
        />
        <Route
          path="/attrition_analysis"
          element={<Navigate to="/hrms/hr-analytics?tab=attritionAnalysis" replace />}
        />
        <Route
          path="/recruitment_funnel"
          element={<Navigate to="/hrms/hr-analytics?tab=recruitmentFunnel" replace />}
        />
        <Route
          path="/salary_trends"
          element={<Navigate to="/hrms/hr-analytics?tab=salaryTrends" replace />}
        />
        <Route
          path="/compliance_reminders"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ComplianceReminders />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add_compliance_reminder"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AddComplianceReminder />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/compliance_reminder_detail/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ComplianceReminderDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit_compliance_reminder/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EditComplianceReminder />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reminder_notification_settings/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ReminderNotificationSettings />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/policy_management" element={<ProtectedRoute requiredPermission="compliance_policy"><MainLayout><CompliancePolicyModule initialTab="policyManagement" /></MainLayout></ProtectedRoute>} />
        <Route path="/new_policy" element={<ProtectedRoute requiredPermission="compliance_policy"><MainLayout><CompliancePolicyModule initialTab="policyLibrary" initialAction="createPolicy" /></MainLayout></ProtectedRoute>} />
        <Route path="/view_policy/:id" element={<ProtectedRoute requiredPermission="compliance_policy"><MainLayout><CompliancePolicyModule initialTab="policyLibrary" initialAction="viewPolicy" /></MainLayout></ProtectedRoute>} />
        <Route path="/edit_policy/:id" element={<ProtectedRoute requiredPermission="compliance_policy"><MainLayout><CompliancePolicyModule initialTab="policyLibrary" initialAction="editPolicy" /></MainLayout></ProtectedRoute>} />
        <Route path="/download_policy/:id" element={<ProtectedRoute requiredPermission="compliance_policy"><MainLayout><CompliancePolicyModule initialTab="policyLibrary" initialAction="viewPolicy" /></MainLayout></ProtectedRoute>} />
        <Route path="/send_policy_reminder/:id" element={<ProtectedRoute requiredPermission="compliance_policy"><MainLayout><CompliancePolicyModule initialTab="policyLibrary" initialAction="editPolicy" /></MainLayout></ProtectedRoute>} />
        <Route path="/policy_settings/:id" element={<ProtectedRoute requiredPermission="compliance_policy"><MainLayout><CompliancePolicyModule initialTab="policyLibrary" initialAction="editPolicy" /></MainLayout></ProtectedRoute>} />
        <Route
          path="/analytics_audit_logs"
          element={
            <ProtectedRoute requiredPermission="reports" requiredAction="read">
              <MainLayout>
                <AnalyticsAuditLogs />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm_sales"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CRMSalesDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/leads"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LeadManagementModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lead_management"
          element={<Navigate to="/crm/leads" replace />}
        />
        <Route
          path="/lead_dashboard"
          element={<Navigate to="/crm/leads?tab=dashboard" replace />}
        />
        <Route
          path="/lead_capture"
          element={<Navigate to="/crm/leads?tab=sources" replace />}
        />
        <Route
          path="/lead_assignment"
          element={<Navigate to="/crm/leads?tab=assignment" replace />}
        />
        <Route
          path="/lead_scoring"
          element={<Navigate to="/crm/leads?tab=list" replace />}
        />
        <Route
          path="/sales_funnel"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Sales Funnel"
                  description="Track leads through sales funnel"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/opportunity_deal"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Opportunity & Deal Flow"
                  description="Manage sales opportunities and deals"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/opportunities"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Opportunities"
                  description="Manage sales opportunities"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/deal_tracking"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Deal Tracking"
                  description="Track deal progress and status"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* <Route
          path="/pipeline_management"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PipelineDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/proposal_quotation"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Proposal & Quotation"
                  description="Create and manage proposals and quotations"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/quote_builder"
          element={
            <ProtectedRoute>
              <MainLayout>
                <QuoteBuilder />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/proposal_templates"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Proposal Templates"
                  description="Manage proposal templates"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/*
      <Route
        path="/orders_logistics"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PlaceholderPage
                title="Orders & Logistics"
                description="Manage orders and logistics operations"
              />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/order_dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OrderDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/order_management"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OrderManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory_sync"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InventorySync />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dispatch_tracking"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DispatchTracking />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/batch_expiry"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BatchExpiryManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      */}
        <Route
          path="/customer_profiles"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CustomerProfiles />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/communication_logs"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Communication Logs"
                  description="Track customer communications"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/multi_channel"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Multi-Channel Integration"
                  description="Integrate multiple communication channels"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticketing_system"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Ticketing System"
                  description="Manage customer support tickets"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* <Route
        path="/campaign_management"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CampaignManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      /> */}
        <Route
          path="/customer_360"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Customer360List />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/customer_360/add"
          element={
            <ProtectedRoute permission="customer_management" action="create">
              <MainLayout>
                <CustomerForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/customer_360/edit/:id"
          element={
            <ProtectedRoute permission="customer_management" action="update">
              <MainLayout>
                <CustomerForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/customer_360/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Customer360 />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* <Route
        path="/comms_inbox"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CommsInbox />
            </MainLayout>
          </ProtectedRoute>
        }
      /> */}
        <Route
          path="/tickets_feedback"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TicketsFeedback />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/tickets/:ticketId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TicketDetails />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Campaigns />
            </MainLayout>
          </ProtectedRoute>
        }
      /> */}
        {/*
      <Route
        path="/caller_popups"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CallerPopups />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      */}
        {/* <Route
        path="/chatbot_self_service"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ChatbotSelfService />
            </MainLayout>
          </ProtectedRoute>
        }
      /> */}
        <Route
          path="/crm_sales_dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CRMSalesDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/convert_opportunity"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ConvertOpportunity />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* ========================================
          LEAD MANAGEMENT MODULE (Canonical Route)
          ======================================== */}
        <Route
          path="/crm/leads"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LeadManagementModule />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* ========================================
          LEGACY LEAD ROUTES (Backward Compatible)
          ======================================== */}
        <Route
          path="/lead_activities"
          element={<Navigate to="/crm/leads?tab=activities" replace />}
        />
        <Route
          path="/lead_assignment_management"
          element={<Navigate to="/crm/leads?tab=assignment" replace />}
        />
        <Route
          path="/lead_360_overview"
          element={<Navigate to="/crm/leads?tab=dashboard" replace />}
        />
        <Route
          path="/lead_detail_360/:leadId"
          element={<LeadDetailRedirect />}
        />
        <Route
          path="/lead_list_filters"
          element={<Navigate to="/crm/leads?tab=list" replace />}
        />
        <Route
          path="/lead_scoring_qualification"
          element={<Navigate to="/crm/leads?tab=scoring" replace />}
        />
        <Route
          path="/lead_sources_capture"
          element={
            <Navigate to="/crm/leads?tab=sources" replace />
          }
        />
        <Route
          path="/manual_lead_entry"
          element={<Navigate to="/crm/leads?tab=list&action=add_lead" replace />}
        />



        {/* <Route
          path="/tender_lead"
          element={<Navigate to="/crm/leads?tab=tenders" replace />}
        /> */}
        <Route
          path="/log_activity/:id"
          element={<LogActivityRedirect />}
        />
        {/* <Route
          path="/opportunity_pipeline"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PipelineDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/close_won_lost"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CloseWonLost />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/forecasts_targets"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ForecastsTargets />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/Opportunity-dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PipelineDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pipeline_settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PipelineSettings />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks_followups"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TasksFollowups />
              </MainLayout>
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/quotation_dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <QuotationDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/send_track"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SendTrack />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/version_history"
          element={
            <ProtectedRoute>
              <MainLayout>
                <VersionHistory />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics_reporting"
          element={
            <ProtectedRoute>
              <MainLayout>
                <HRAnalyticsDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm_analytics_reporting"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CRMAnalytics />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales_dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SalesDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* <Route
          path="/quote_order_dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <QuoteOrderDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        /> */}

        {/* ── NEW: Shipway Shipment Tracking & Analytics ── */}
        {/**
      <Route
        path="/crm/logistics/shipments"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ShipmentAnalytics />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      */}
        {/* ─────────────────────────────────────────────── */}

        <Route
          path="/party_financials"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PartyFinancials />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/targets_dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TargetsDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/target_builder"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TargetBuilder />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/created_targets"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CreatedTargets />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/target/:targetId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TargetDetails />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/schedules"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Schedules />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/org_overview"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Organization Overview"
                  description="High-level organizational metrics and KPIs"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hr_dashboards"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="HR Dashboards"
                  description="Human resource analytics and insights"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales_dashboards"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Sales Dashboards"
                  description="Sales performance and CRM analytics"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/custom_reports"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Custom Reports"
                  description="Build and manage custom reports"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/data_export"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlaceholderPage
                  title="Data Export"
                  description="Export data in various formats"
                />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mydashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my_profile"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyProfile />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my_attendance"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyAttendance />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my_wfh_requests"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyWFHRequests />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/apply_leave"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ApplyLeave />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my_payslips"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyPayslips />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my_payslips/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PayslipDetailsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my_training"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyTraining />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my_performance"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyPerformance />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/policies"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PoliciesCompliance />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/submit_resignation"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SubmitResignation />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my_interviews"
          element={
            <ProtectedRoute>
              <MainLayout>
                <InterviewScheduling isEmployeeView={true} />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Notifications />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/wfh_approval_panel"
          element={
            <ProtectedRoute>
              <MainLayout>
                <WorkFromHomeApprovals />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/career" element={<Career />} />
        {/* Public Quotation View (No Auth Required) */}
        <Route path="/public/quote/:quoteId" element={<PublicQuotationView />} />
        {/* Public Ticket View (No Auth Required) */}
        <Route path="/public/ticket/:ticketId" element={<PublicTicketView />} />
        {/* LinkedIn OAuth Callback (No Auth Required - LinkedIn redirects here) */}
        <Route path="/linkedin/callback" element={<LinkedInCallback />} />
        <Route
          path="/api/offer/:id/response"
          element={<OfferAcceptance />}
        />
        <Route
          path="/api/offer/:id/upload-documents"
          element={<OfferAcceptance />}
        />
        <Route
          path="/offer-acceptance/:id"
          element={<OfferAcceptance />}
        />
        <Route
          path="/candidate-document-upload/:candidateId"
          element={<CandidateDocumentUploadPortal />}
        />
        <Route
          path="/"
          element={isAuthenticated ? <LoginRedirectRouter /> : <Navigate to="/login" replace />}
        />
        <Route
          path="*"
          element={
            isAuthenticated ? <LoginRedirectRouter /> : <Navigate to="/login" replace />
          }
        />
      </Routes>
    </React.Suspense>
  );
};

// Main App Component
const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SocketProvider>
          <LeadProvider>
            <NotificationProvider>
              <WithCustomAlerts>
                <Router>
                  <ScrollToTop />
                  <div className="App">
                    <AppRoutes />
                  </div>
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#363636',
                        color: '#fff',
                        zIndex: 99999
                      },
                      success: {
                        duration: 6000,
                        style: {
                          background: '#22c55e',
                          color: '#fff',
                        },
                      },
                      error: {
                        duration: 5000,
                        style: {
                          background: '#f59e0b',
                        },
                      },
                    }}
                  />
                </Router>
              </WithCustomAlerts>
            </NotificationProvider>
          </LeadProvider>
        </SocketProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
